// The metronome's panel. It plays on the dock's clock, so it has the same
// tempo as the backing track and can click along with it.

import React from 'react';
import classNames from 'classnames';
import { MAX_BPM, MIN_BPM } from '../backing-track/engine';
import { track } from '../../usage';
import { MAX_BEATS, SUBDIVISIONS } from './use-transport';
import { Segmented, TempoControl } from './controls';
import { KeyHint, spaceLabel } from './shortcuts';

const subdivisionName = n => ({
    1: gettext('Quarter notes'),
    2: gettext('Eighth notes'),
    3: gettext('Triplets'),
    4: gettext('Sixteenth notes'),
}[n]);
const SUBDIVISION_SYMBOLS = { 1: '♩', 2: '♫', 3: '3', 4: '♬' };

const accentName = level => [gettext('Silent'), gettext('Normal'), gettext('Accent')][level];

const TRAINER_STEPS = [1, 2, 3, 5, 10];
const TRAINER_BARS = [1, 2, 4, 8, 16];

// The speed trainer is for subscribers (see useTransport).
export default function Metronome({ transport, bandBeats, looping, isSubscriber, onRequireSubscription }) {
    const { metronome, click, band, beat, trainerOn } = transport;
    const { trainer } = metronome;

    const toggleTrainer = on => {
        if (on && !isSubscriber) {
            onRequireSubscription();
            return;
        }
        transport.setMetronome({ trainer: { on } });
        if (on) {
            track('speed_trainer_started');
        }
    };
    const beats = band ? bandBeats : metronome.beats;
    const current = click && beat && beat.bar >= 0 ? beat.beat : null;

    return (
        <div className="metronome practice-tool">
            <div className="metronome-transport">
                <button
                    type="button"
                    className={classNames('practice-play', { playing: click })}
                    onClick={click ? transport.stopClick : transport.startClick}
                    aria-label={click ? gettext('Stop') : gettext('Start')}
                >
                    {click ? <span className="icon-stop" /> : <span className="icon-play" />}
                    <KeyHint k={spaceLabel()} />
                </button>
                <TempoControl transport={transport} large />
            </div>

            <input
                type="range"
                className="metronome-slider"
                min={MIN_BPM}
                max={MAX_BPM}
                value={transport.bpm}
                aria-label={gettext('Tempo')}
                onChange={e => transport.setBpm(Number(e.target.value))}
            />

            <div className="metronome-beats" style={{ '--beats': beats }}>
                {Array.from({ length: beats }, (_, i) => {
                    const level = band ? (i === 0 ? 2 : 1) : metronome.accents[i];
                    return (
                        <button
                            key={i}
                            type="button"
                            className={classNames('metronome-beat', `level-${level}`, { now: current === i })}
                            disabled={band}
                            onClick={() => transport.cycleAccent(i)}
                            aria-label={`${i + 1}: ${accentName(level)}`}
                            title={gettext('Tap to change the accent')}
                        >
                            {i + 1}
                        </button>
                    );
                })}
            </div>

            {band ?
                <p className="practice-hint">{interpolate(gettext('Clicking along with the backing track, %s beats to the bar.'), [bandBeats])}</p>
                :
                <div className="practice-row">
                    <label className="practice-inline">
                        {gettext('Beats per bar')}
                        <select className="practice-select" value={metronome.beats} onChange={e => transport.setMetronome({ beats: Number(e.target.value) })}>
                            {Array.from({ length: MAX_BEATS }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                        </select>
                    </label>
                    <Segmented
                        label={gettext('Clicks per beat')}
                        value={metronome.subdivision}
                        onChange={subdivision => transport.setMetronome({ subdivision })}
                        options={SUBDIVISIONS.map(n => ({ value: n, label: SUBDIVISION_SYMBOLS[n], title: subdivisionName(n) }))}
                    />
                </div>
            }

            <div className="practice-row">
                <label className="practice-inline metronome-volume">
                    {gettext('Volume')}
                    <input type="range" min="0" max="1" step="0.05" value={metronome.volume} onChange={e => transport.setMetronome({ volume: Number(e.target.value) })} />
                </label>
            </div>

            <div className="practice-section">
                <label className="practice-check">
                    <input type="checkbox" checked={trainerOn} onChange={e => toggleTrainer(e.target.checked)} />
                    {gettext('Speed trainer')}
                </label>
                {trainerOn &&
                    <div className="practice-inline practice-indent">
                        <select className="practice-select" aria-label={gettext('BPM faster each time')} value={trainer.step} onChange={e => transport.setMetronome({ trainer: { step: Number(e.target.value) } })}>
                            {TRAINER_STEPS.map(step => <option key={step} value={step}>+{step} BPM</option>)}
                        </select>
                        <select className="practice-select" aria-label={gettext('How often')} value={trainer.every} onChange={e => transport.setMetronome({ trainer: { every: e.target.value === 'loop' ? 'loop' : Number(e.target.value) } })}>
                            {TRAINER_BARS.map(bars => <option key={bars} value={bars}>{interpolate(ngettext('every bar', 'every %s bars', bars), [bars])}</option>)}
                            {(looping || trainer.every === 'loop') && <option value="loop">{gettext('each time round the loop')}</option>}
                        </select>
                        {gettext('up to')}
                        <input
                            type="number"
                            className="practice-number"
                            min={MIN_BPM}
                            max={MAX_BPM}
                            value={trainer.to}
                            aria-label={gettext('Fastest tempo')}
                            onChange={e => transport.setMetronome({ trainer: { to: Number(e.target.value) || trainer.to } })}
                        />
                        BPM
                    </div>
                }
                {trainerOn && <p className="practice-hint">{gettext('The tempo goes back to where it started when you stop.')}</p>}
            </div>
        </div>
    );
}
